import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { RiCloseLine, RiPlayFill, RiPauseFill, RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import LanguageContext from '../../context/LanguageContext.js';

const SLIDE_MS = 5000;
const SWIPE_X = 50;
const SWIPE_DOWN = 90;
const MAX_SEGMENTS = 20;

/**
 * Full-screen, story-style photo viewer: blurred colour-matched backdrop, segmented progress,
 * optional slideshow, tap zones, swipe (left/right to navigate, down to close) and keyboard.
 */
export default function StoryViewer({ photos, index, autoplay = false, onChange, onClose }) {
    const { t } = useTranslation();
    const { dir } = useContext(LanguageContext);
    const isRtl = dir() === 'rtl';
    const [playing, setPlaying] = useState(autoplay);
    const [hidden, setHidden] = useState(false);
    const [dragY, setDragY] = useState(0);
    const rootRef = useRef(null);
    const closeRef = useRef(null);
    const touch = useRef(null);
    const total = photos.length;
    const photo = photos[index];

    const go = useCallback((delta) => onChange((index + delta + total) % total), [index, total, onChange]);

    // Scroll lock + initial focus.
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeRef.current?.focus();
        return () => {
            document.body.style.overflow = previous;
        };
    }, []);

    // Pause the slideshow while the tab is hidden.
    useEffect(() => {
        const sync = () => setHidden(document.hidden);
        document.addEventListener('visibilitychange', sync);
        return () => document.removeEventListener('visibilitychange', sync);
    }, []);

    // Keyboard: Esc, arrows (mirrored in RTL), Space toggles the slideshow, Tab stays inside.
    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            } else if (event.key === 'ArrowRight') {
                go(isRtl ? -1 : 1);
            } else if (event.key === 'ArrowLeft') {
                go(isRtl ? 1 : -1);
            } else if (event.key === ' ' && event.target?.tagName !== 'BUTTON') {
                event.preventDefault();
                setPlaying((value) => !value);
            } else if (event.key === 'Tab' && rootRef.current) {
                const focusable = rootRef.current.querySelectorAll('button:not([disabled])');
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [go, isRtl, onClose]);

    // Slideshow timing runs on a timer, not the progress bar's animationend: animations can be
    // throttled or switched off (reduced-motion overrides), which must not stall the slideshow.
    const running = playing && !hidden && total > 1;
    useEffect(() => {
        if (!running) return undefined;
        const timer = setTimeout(() => go(1), SLIDE_MS);
        return () => clearTimeout(timer);
    }, [running, index, go]);

    // Preload neighbours so moving through the story is instant.
    useEffect(() => {
        if (total < 2) return;
        [index + 1, index - 1].forEach((i) => {
            const neighbour = photos[(i + total) % total];
            if (neighbour) new Image().src = neighbour.src;
        });
    }, [index, photos, total]);

    const onTouchStart = (event) => {
        const point = event.touches[0];
        touch.current = { x: point.clientX, y: point.clientY };
    };

    const onTouchMove = (event) => {
        if (!touch.current) return;
        const dy = event.touches[0].clientY - touch.current.y;
        const dx = event.touches[0].clientX - touch.current.x;
        if (dy > 0 && Math.abs(dy) > Math.abs(dx)) setDragY(dy);
    };

    const onTouchEnd = (event) => {
        if (!touch.current) return;
        const point = event.changedTouches[0];
        const dx = point.clientX - touch.current.x;
        const dy = point.clientY - touch.current.y;
        touch.current = null;
        setDragY(0);
        if (dy > SWIPE_DOWN && dy > Math.abs(dx)) {
            onClose();
        } else if (Math.abs(dx) > SWIPE_X && Math.abs(dx) > Math.abs(dy)) {
            // Swiping left shows the next photo (mirrored in RTL).
            go((dx < 0) !== isRtl ? 1 : -1);
        }
    };

    if (!photo) return null;

    const PrevIcon = isRtl ? RiArrowRightSLine : RiArrowLeftSLine;
    const NextIcon = isRtl ? RiArrowLeftSLine : RiArrowRightSLine;
    const useSegments = total > 1 && total <= MAX_SEGMENTS;

    return createPortal(
        <div
            ref={rootRef}
            className="sv-root"
            role="dialog"
            aria-modal="true"
            aria-label={t('gallery.viewerLabel')}
            dir={isRtl ? 'rtl' : 'ltr'}
            style={{ '--sv-drag': `${dragY}px`, '--sv-fade': Math.max(0.4, 1 - dragY / 400) }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            {/* Colour-matched ambient backdrop */}
            <div key={`bg-${photo.id}`} className="sv-backdrop" style={{ backgroundImage: `url(${JSON.stringify(photo.src)})` }} aria-hidden="true" />
            <div className="sv-scrim" aria-hidden="true" />

            {/* Progress */}
            <div className="sv-progress" aria-hidden="true">
                {useSegments ? (
                    photos.map((item, i) => (
                        <span key={item.id} className={`sv-segment${i < index ? ' sv-segment--done' : ''}`}>
                            {i === index && (
                                <span
                                    key={`${item.id}-${running}`}
                                    className={`sv-segment-fill${running ? ' sv-segment-fill--running' : ''}`}
                                    style={{ animationDuration: `${SLIDE_MS}ms` }}
                                />
                            )}
                        </span>
                    ))
                ) : (
                    total > 1 && (
                        <span className="sv-segment">
                            <span
                                key={`${photo.id}-${running}`}
                                className={`sv-segment-fill${running ? ' sv-segment-fill--running' : ''}`}
                                style={{ animationDuration: `${SLIDE_MS}ms` }}
                            />
                        </span>
                    )
                )}
            </div>

            {/* Top bar */}
            <div className="sv-top">
                <span className="sv-counter" aria-live="polite">
                    {t('gallery.counter', { current: index + 1, total })}
                </span>
                <div className="sv-actions">
                    {total > 1 && (
                        <button
                            type="button"
                            className="sv-icon-btn"
                            onClick={() => setPlaying((value) => !value)}
                            aria-label={playing ? t('gallery.pause') : t('gallery.play')}
                            aria-pressed={playing}
                        >
                            {playing ? <RiPauseFill aria-hidden="true" /> : <RiPlayFill aria-hidden="true" />}
                        </button>
                    )}
                    <button
                        ref={closeRef}
                        type="button"
                        className="sv-icon-btn"
                        onClick={onClose}
                        aria-label={t('gallery.closeLightbox')}
                    >
                        <RiCloseLine aria-hidden="true" />
                    </button>
                </div>
            </div>

            {/* Photo */}
            <figure className="sv-stage">
                <img
                    key={photo.id}
                    src={photo.src}
                    alt={photo.description || t('gallery.imageFallback')}
                    className="sv-photo"
                    draggable={false}
                />
            </figure>

            {/* Tap zones (story-style) + visible arrows on larger screens */}
            {total > 1 && (
                <>
                    <button type="button" className="sv-zone sv-zone--prev" onClick={() => go(-1)} aria-label={t('gallery.prev')}>
                        <span className="sv-arrow" aria-hidden="true"><PrevIcon /></span>
                    </button>
                    <button type="button" className="sv-zone sv-zone--next" onClick={() => go(1)} aria-label={t('gallery.next')}>
                        <span className="sv-arrow" aria-hidden="true"><NextIcon /></span>
                    </button>
                </>
            )}

            {photo.description && (
                <div className="sv-caption">
                    <p>{photo.description}</p>
                </div>
            )}
        </div>,
        document.body
    );
}
